import { defineComponent as _defineComponent } from 'vue'
import { computed, defineAsyncComponent, onMounted, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { prop, path, uniqBy, whereEq } from 'ramda'
import { watchDebounced } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { useGetAnOfferStore } from '@/components/forms/equipmentGetAnOfferCta'
import { COMMERCIAL_OFFERS_CREATE } from '@m/cabinet/routes'
import { useSubmit, nestedValidatorErrorsParser } from '@/composables/useSubmit'
import { useLoadingState } from '@/packages/vue-loading-state'
import { useSelectsDynamicOptions } from '@/composables/useSelectsDynamicOptions'
import { paymentCalculatorApi } from '@/services/paymentCalculator.service'
import { pluralization } from '@/utils/intl-helpers'
import { commercialOfferTransformFrom } from '@/modules/cabinet/modules/CommercialOffer/transformers'
import { commercialOffersApi } from '@/services/commercialOffers.service'
import { useModalOpener } from '@/composables/useModalOpener'
import { isPermittedSimple } from '@/modules/user/permissions'
import { OFFER } from '@/modules/user/accessTokens'
import { catalogApi } from '@/services/catalog.service'
import {
  OfferStatusActivity,
  OfferStatusModeration,
} from '@/modules/cabinet/modules/Offers/const'
import { COMMERCIAL_OFFER_SOURCE_TYPE } from '@/const'
import { IS_DEALER, IS_AGENT, IS_MANAGER, IS_CUSTOMER } from '@/modules/user/accessTokens'


export default /*@__PURE__*/_defineComponent({
  __name: 'equipmentOffer',
  props: {
    offerId: {},
    offerUuid: {},
    offer: {},
    price_monthly: {},
    duration: {},
    down_payment: {},
    price_amount: {},
    main_phone: {},
    is_owner: { type: Boolean },
    is_machinery_owner: { type: Boolean },
    close: { type: Function },
    userName: {},
    userEmail: {},
    userPhone: {}
  },
  setup(__props: any, { expose: __expose }) {
  __expose();

const EquipmentGetAnOffer = defineAsyncComponent(
  () => import('@/components/forms/equipmentGetAnOffer.vue')
)
const EquipmentBuyNow = defineAsyncComponent(
  () => import('@/components/forms/equipmentBuyNow.vue')
)

const props = __props

const defaultSliderOptions = {
  'tooltip-placement': 'bottom',
  contained: true,
  'dot-size': 16,
}

const router = useRouter()
const { t } = useI18n()

onMounted(() => {
  if (props.offerId && props.offer) {
    form.value = {
      ...form.value,
      ...props.offer,
      offer: { uuid: props.offer.uuid },
      period: props.duration,
      downPayment: props.down_payment,
      priceAmount: props.price_amount,
      sourceType: COMMERCIAL_OFFER_SOURCE_TYPE.offer
    }
    result.value.priceMonthly = props.price_monthly
  }
  getCalculatorConfig({
    location_country_id: form.value.locationCountry?.id,
    target_country_id: undefined,
    category_id: form.value.category?.id,
    offer_id: props.offerId,
  })
})

const { IS_AUTH = false, REMOTE_CONFIG = {}, applying_for_financing_url } = window

const { use_future_applying_for_financing = false } = REMOTE_CONFIG

const labels = ref({
  targetCountry: t('Delivery country'),
  period: t('Financing period, months'),
  downPayment: t('Down payment, €'),
})

const FinancingPeriodPlurals = {
  one: t('month'),
  few: t('months'),
  many: t('months'),
}
const pluralFinancingPeriodMin = computed(() =>
  pluralization(FinancingPeriodPlurals, currentConfig.value?.leasePeriodMin)
)
const pluralFinancingPeriodMax = computed(() =>
  pluralization(FinancingPeriodPlurals, currentConfig.value?.leasePeriodMax)
)

const isLoad = ref(false)
const loadingState = useLoadingState()

const result = ref({})

const urlForApplyingFinancing = computed(() => {

  const query = new URLSearchParams()
  query.set('offer_id', props.offerUuid)
  query.set('target_country_id', form.value.targetCountry?.uuid)
  query.set('down_payment', form.value.downPayment)
  query.set('period', form.value.period)
  query.set('financing_type', form.value.financingType?.id)
  query.set('schedule_type', form.value.scheduleType?.id)
  query.set('price_monthly', result.value?.priceMonthly)
  return applying_for_financing_url + '?' + query.toString()
})

function redirectToApplyingForFinancing() {
  formRef.value.validate().then(({ valid }) => {
    if (valid && form.value.targetCountry && form.value.targetCountry.id) {
      window.open(urlForApplyingFinancing.value, '_blank')
    } else {
      isShowCountryError.value = true
    }
  })
}

const form = ref({
  period: Number(props.duration),
  downPayment: Number(props.down_payment),
  sourceType: COMMERCIAL_OFFER_SOURCE_TYPE.offer
})
const formRef = ref(null)
const calculatorConfig = ref(null)

const scheduleTypes = ref([])
const financingTypes = ref([])
const allCalculatorConfig = ref([])

const currentConfig = computed(() => {
  if (form.value.scheduleType && form.value.financingType) {
    return allCalculatorConfig.value.find((config) => {
      const { scheduleType, financingType } = config
      const findCurrent = whereEq({
        scheduleType: form.value?.scheduleType,
        financingType: form.value?.financingType,
      })
      return findCurrent(config)
    })?.config
  }
  return null
})

const downPaymentConfig = computed(() => {
  if (currentConfig.value) {
    return {
      min: Math.round(
        (form.value.priceAmount || 0) * currentConfig.value.downPaymentMin
      ),
      max: Math.round(
        (form.value.priceAmount || 0) * currentConfig.value.downPaymentMax
      ),
    }
  }
  return null
})

const countriesTargetFilters = computed(() => ({
  location_country_id: form.value.locationCountry?.id,
  category_id: form.value.category?.id,
  financing_type: form.value.financingType?.id,
  is_root: true,
}))
const {
  loading: targetCountriesLoading,
  list: targetCountriesList,
  load: targetCountriesLoad,
  debounceSearch: targetCountriesSearch,
  get: targetCountriesGet,
} = useSelectsDynamicOptions(
  paymentCalculatorApi.countriesTargetList,
  countriesTargetFilters
)

function getCalculatorConfig(params) {
  loadingState
    .load(
      (params.target_country_id
        ? paymentCalculatorApi.configs
        : paymentCalculatorApi.defaultConfigs
      ).execute(params)
    )
    .then(({ items }) => {
      allCalculatorConfig.value = items
      scheduleTypes.value = uniqBy(
        prop('id'),
        items.map((el) => el.scheduleType)
      )
      financingTypes.value = uniqBy(
        prop('id'),
        items.map((el) => el.financingType)
      )
      if (isChangeConfig.value || !props.initial?.financingType) {
        form.value.financingType = financingTypes.value[0] || null
      }
      if (isChangeConfig.value || !props.initial?.scheduleType) {
        form.value.scheduleType = scheduleTypes.value[0] || null
      }
    })
}
const isChangeConfig = ref<boolean>(false)

watch(
  () => form.value.targetCountry?.id,
  () => {
    isShowCountryError.value = false
    isChangeConfig.value = true
    getCalculatorConfig({
      location_country_id: form.value.locationCountry?.id,
      target_country_id: form.value.targetCountry?.id,
      category_id: form.value.category?.id,
      offer_id: props.offerId,
    })
  }
)
let isInitialize = false
watchDebounced(
  [
    () => form.value.targetCountry?.id,
    () => form.value.period,
    () => form.value.downPayment,
  ],
  ([newTargetCountry]) => {
    if (isInitialize) {
      if (newTargetCountry) {
        request(form.value, formRef.value)
      } else {
        isShowCountryError.value = true
        formRef.value.setFieldError('targetCountry', t('This field is required'))
      }
    } else {
      isInitialize = true
    }
  },
  { debounce: 500 }
)

let configChanged = false

watch(currentConfig, (newConfig) => {
  if (configChanged) {
    form.value.period = newConfig?.leasePeriodDefault
    form.value.downPayment =
      Math.round(form.value.priceAmount * newConfig?.downPaymentDefault) || 0
  } else {
    configChanged = true
    // form.value.period = props.default
    // form.value.downPayment =
    //   Math.round(form.value.priceAmount * newConfig?.downPaymentMin) || 0
  }
  // form.value.agencyFee = Math.round(
  //   (form.value.priceAmount - form.value.downPayment) *
  //     newConfig?.agencyFeeDefault
  // )
})

const request = async (sendData, control = formRef.value) => {
  formRef.value.setFieldError?.('nonFieldErrors', '')
  await control.validate().then(async ({ valid }) => {
    if (valid) {
      isLoad.value = true
      return await useSubmit(
        send(commercialOfferTransformFrom(form.value)),
        control,
        nestedValidatorErrorsParser
      )
    }
  })
}

const send = async (sendData) => {
  try {
    const {
      data: { item },
    } = await commercialOffersApi.getPriceMonthly.execute(
      { id: props.initial?.id },
      {
        ...sendData,
        title: props.offer.title || 'dummy',
      }
    )
    result.value = item
    return item
  } catch (e) {
    isLoad.value = false
    return Promise.reject(e)
  }
}

const isShowCountryError = ref<boolean>(false)

function openGetAnOfferModal(isCustomer = false) {
  const user = {
    contactName: props.userName,
    contactEmail: props.userEmail,
    contactPhone: props.userPhone,
  }
  const { open } = useModalOpener({
    name: 'EquipmentGetAnOffer',
    component: EquipmentGetAnOffer,
    initial: {
      ...form.value,
      ...(isCustomer ? user : {}),
    },
    classes: ['modal--size_lg'],
  })
  if (form.value.targetCountry && form.value.targetCountry.id) {
    formRef.value.validate().then(({ valid }) => {
      if (valid) {
        open()
      }
    })
  } else {
    isShowCountryError.value = true
  }
}

function openBuyNowModal() {
  const { open } = useModalOpener({
    name: 'EquipmentBuyNow',
    component: EquipmentBuyNow,
    initial: form.value,
    classes: ['modal--size_lg'],
  })
  if (form.value.targetCountry && form.value.targetCountry.id) {
    open()
  } else {
    isShowCountryError.value = true
  }
}

function callUsHandler() {
  catalogApi.offers.callUsClick.execute(
    {},
    {
      offerId: props.offerId,
    }
  )
}

// CTA action
const getAnOfferStore = useGetAnOfferStore()
const targetCountryRef = ref(null)
getAnOfferStore.action = () => {
  // if (use_future_applying_for_financing) {
  //   redirectToApplyingForFinancing()
  // } else {
  //   openBuyNowModal()
  // }
  if (isShowCountryError.value) {
    formRef.value.setFieldError('targetCountry', t('This field is required'))
    nextTick(() => {
      targetCountryRef.value?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      })
    })
  }
  openGetAnOfferModal(isPermittedSimple([IS_CUSTOMER]))
}

const __returned__ = { EquipmentGetAnOffer, EquipmentBuyNow, props, defaultSliderOptions, router, t, IS_AUTH, REMOTE_CONFIG, applying_for_financing_url, use_future_applying_for_financing, labels, FinancingPeriodPlurals, pluralFinancingPeriodMin, pluralFinancingPeriodMax, isLoad, loadingState, result, urlForApplyingFinancing, redirectToApplyingForFinancing, form, formRef, calculatorConfig, scheduleTypes, financingTypes, allCalculatorConfig, currentConfig, downPaymentConfig, countriesTargetFilters, targetCountriesLoading, targetCountriesList, targetCountriesLoad, targetCountriesSearch, targetCountriesGet, getCalculatorConfig, isChangeConfig, get isInitialize() { return isInitialize }, set isInitialize(v) { isInitialize = v }, get configChanged() { return configChanged }, set configChanged(v) { configChanged = v }, request, send, isShowCountryError, openGetAnOfferModal, openBuyNowModal, callUsHandler, getAnOfferStore, targetCountryRef, computed, defineAsyncComponent, onMounted, nextTick, ref, watch, get useI18n() { return useI18n }, get prop() { return prop }, get path() { return path }, get uniqBy() { return uniqBy }, get whereEq() { return whereEq }, get watchDebounced() { return watchDebounced }, get useRouter() { return useRouter }, get useGetAnOfferStore() { return useGetAnOfferStore }, get COMMERCIAL_OFFERS_CREATE() { return COMMERCIAL_OFFERS_CREATE }, get useSubmit() { return useSubmit }, get nestedValidatorErrorsParser() { return nestedValidatorErrorsParser }, get useLoadingState() { return useLoadingState }, get useSelectsDynamicOptions() { return useSelectsDynamicOptions }, get paymentCalculatorApi() { return paymentCalculatorApi }, get pluralization() { return pluralization }, get commercialOfferTransformFrom() { return commercialOfferTransformFrom }, get commercialOffersApi() { return commercialOffersApi }, get useModalOpener() { return useModalOpener }, get isPermittedSimple() { return isPermittedSimple }, get OFFER() { return OFFER }, get catalogApi() { return catalogApi }, get OfferStatusActivity() { return OfferStatusActivity }, get OfferStatusModeration() { return OfferStatusModeration }, get COMMERCIAL_OFFER_SOURCE_TYPE() { return COMMERCIAL_OFFER_SOURCE_TYPE }, get IS_DEALER() { return IS_DEALER }, get IS_AGENT() { return IS_AGENT }, get IS_MANAGER() { return IS_MANAGER }, get IS_CUSTOMER() { return IS_CUSTOMER } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})